import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./games.css";
// Game Components
import MemoryGame from "./MemoryGame";
import CarromGame from "./CarromGame";
import WordScramble from "./WordScramble";
import BalloonShooter from "./BalloonShooter";
import BreakoutGame from "./BreakoutGame";
import FruitCatcher from "./FruitCatcher";
// New Games
import TrafficEscape from "./TrafficEscape";
import CyberBike from "./CyberBike";
import PenaltyShootout from "./PenaltyShootout";
import BasketballFlick from "./BasketballFlick";
// Assets (New Aesthetic Thumbnails)
import sudokuImg from "../assets/sudoku_elite_thumb.png";
import t2048Img from "../assets/nexus_2048_thumb.png";
import snakeImg from "../assets/cyber_snake_thumb.png";
import carromThumb from "../assets/carrom_thumb.png";
import balloonThumb from "../assets/balloon_shoot_thumb_new.png";
import basketballThumb from "../assets/basketball_thumb.png";
import cyberBikeThumb from "../assets/cyber_bike_thumb.png";
import HomeTaboola from "../../../subcomponents/HomeTaboola";

const getCategoryIcon = (category) => {
  switch (category) {
    case "All":
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
        </svg>
      );
    case "Arcade":
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="6" width="20" height="12" rx="3" />
          <line x1="6" y1="12" x2="10" y2="12" />
          <line x1="8" y1="10" x2="8" y2="14" />
          <line x1="15" y1="13" x2="15.01" y2="13" />
          <line x1="18" y1="11" x2="18.01" y2="11" />
        </svg>
      );
    case "Puzzle":
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
        </svg>
      );
    case "Sports":
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M6 12A6 6 0 0 1 18 12" />
          <path d="M12 6A6 6 0 0 1 12 18" />
        </svg>
      );
    case "Racing":
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
        </svg>
      );
    case "Brain":
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
          <path d="M9 18h6M10 22h4" />
        </svg>
      );
    default:
      return null;
  }
};



const Games = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [playingGame, setPlayingGame] = useState(gameId || null);
  const [activeFilter, setActiveFilter] = useState("All");
  const [isPaused, setIsPaused] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".g_filter-dropdown-container")) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (gameId) {
      setPlayingGame(gameId);
    } else {
      setPlayingGame(null);
    }
  }, [gameId]);

  // Allow scrolling naturally but prevent arrow key scrolling when a game is active  
  useEffect(() => {
    if (playingGame) {
      document.body.style.overflow = "hidden";
      document.body.classList.add("games-fullscreen");
    } else {
      document.body.style.overflow = "auto";
      document.body.classList.remove("games-fullscreen");
    }

    const preventScroll = (e) => {
      if (playingGame && ["ArrowUp", "ArrowDown", " "].includes(e.key)) {
        e.preventDefault();
      }
    };

    if (playingGame) {
      window.addEventListener("keydown", preventScroll, { passive: false });
    }

    return () => {
      document.body.style.overflow = "auto";
      document.body.classList.remove("games-fullscreen");
      window.removeEventListener("keydown", preventScroll);
    };
  }, [playingGame]);

  const allGames = [
    {
      id: "wordscramble",
      name: "Word Scramble",
      category: "Puzzle",
      rating: 4.8,
      image: t2048Img,
      description: "Unscramble the letters to find the hidden word before time runs out!",
      difficulty: "Medium",
    },
    {
      id: "carrom",
      name: "Pro Carrom Master",
      category: "Arcade",
      rating: 4.9,
      image: carromThumb,
      description: "Classic board game with realistic physics and smooth controls.",
      difficulty: "Medium",
    },
    {
      id: "memory",
      name: "Memory Match",
      category: "Brain",
      rating: 4.7,
      image: sudokuImg,
      description: "Test your memory by matching pairs of beautiful icons.",
      difficulty: "Easy",
    },

    {
      id: "basketball",
      name: "Mini Basketball Flick",
      category: "Sports",
      rating: 4.6,
      image: basketballThumb,
      description: "Flick to score baskets in an endless challenge.",
      difficulty: "Medium",
    },

    // {
    //   id: "tabletennis",
    //   name: "Table Tennis Smash",
    //   category: "Sports",
    //   rating: 4.8,
    //   image: tableTennisThumb,
    //   description: "Fast-paced swipe mechanics for addictive action.",
    //   difficulty: "Hard",
    // },

    {
      id: "paranoid",
      name: "Paranoid",
      category: "Arcade",
      rating: 4.9,
      image: snakeImg,
      description: "Classic breakout action. Destroy the brick wall and survive the rounds.",
      difficulty: "Hard",
    },
    {
      id: "bike",
      name: "Cyber Bike Racer",
      category: "Racing",
      rating: 4.8,
      image: cyberBikeThumb,
      description: "Fast-paced neon bike racing. Dodge obstacles at light speed!",
      difficulty: "Medium",
    },
    {
      id: "balloon",
      name: "Balloon Shooter",
      category: "Arcade",
      rating: 4.9,
      image: balloonThumb,
      description: "Aim, stretch, and shoot down as many balloons as you can!",
      difficulty: "Medium",
    },

    {
      id: "fruitcatcher",
      name: "Fruit Catcher",
      category: "Arcade",
      rating: 4.9,
      image: snakeImg,
      description: "Relaxing and highly addictive fruit catching game.",
      difficulty: "Easy",
    },
  ];

  const categories = ["All", "Arcade", "Puzzle", "Sports", "Racing", "Brain"];

  // Replaced useEffect with useMemo for filtering
  const filteredGames = useMemo(() => {
    return activeFilter === "All"
      ? allGames
      : allGames.filter(game => game.category === activeFilter);
  }, [activeFilter, allGames]);

  const handlePlay = (id) => {
    if (window.location.pathname.startsWith("/appgames")) {
      navigate(`/appgames/${id}`);
    } else {
      navigate(`/games/${id}`);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    if (window.location.pathname.startsWith("/appgames")) {
      navigate("/appgames");
    } else {
      navigate("/games");
    }
  };

  const renderActiveGame = () => {
    const gameProps = { onBack: handleBack, isPaused, setIsPaused };
    switch (playingGame) {
      case "memory": return <MemoryGame {...gameProps} />;
      case "carrom": return <CarromGame {...gameProps} />;
      case "wordscramble": return <WordScramble {...gameProps} />;
      case "balloon": return <BalloonShooter {...gameProps} />;
      case "paranoid": return <BreakoutGame {...gameProps} />;
      case "fruitcatcher": return <FruitCatcher {...gameProps} />;
      case "traffic": return <TrafficEscape {...gameProps} />;
      case "bike": return <CyberBike {...gameProps} />;
      case "penalty": return <PenaltyShootout {...gameProps} />;
      case "basketball": return <BasketballFlick {...gameProps} />;
      default: return null;
    }
  };

  return (
    <>
    <div className={`g_games-page ${playingGame ? "is-playing" : ""}`}>
      {playingGame && (
        <style>{`
          body { 
            overflow: hidden !important; 
            height: 100vh !important;
            width: 100vw !important;
            position: fixed !important;
          }
          .g_active-game-wrapper {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            z-index: 9999999 !important;
            background: #000 !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: center !important;
            align-items: center !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .g_active-game-section {
            width: 100% !important;
            height: 100% !important;
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
            overflow: hidden !important;
          }
          .g_active-game-section > div {
            height: 100% !important;
            width: 100% !important;
            max-width: none !important;
            max-height: none !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: center !important;
            align-items: center !important;
            border-radius: 0 !important;
            background-color: inherit !important;
          }
          .g_active-game-section canvas {
            flex: 1 !important;
            height: 100% !important;
            width: auto !important;
            max-height: 100% !important;
            max-width: 100% !important;
            object-fit: contain !important;
            display: block !important;
            margin: 0 auto !important;
          }
          /* Fix for games with fixed wrappers */
          .g_active-game-section [class*="container"],
          .g_active-game-section [class*="wrapper"] {
             height: 100% !important;
             width: 100% !important;
             max-width: none !important;
             max-height: none !important;
             display: flex !important;
             flex-direction: column !important;
             justify-content: center !important;
             align-items: center !important;
          }

          /* Premium Pause Button Styles */
          .g_premium-pause-btn {
            background: rgba(15, 23, 42, 0.75) !important;
            border: 1px solid rgba(255, 255, 255, 0.15) !important;
            color: #f1f5f9 !important;
            padding: 4px 12px 4px 4px !important;
            border-radius: 100px !important;
            cursor: pointer !important;
            backdrop-filter: blur(12px) !important;
            -webkit-backdrop-filter: blur(12px) !important;
            font-weight: 800 !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 6px !important;
            font-size: 10px !important;
            font-family: var(--font-display, 'Outfit', sans-serif) !important;
            letter-spacing: 1px !important;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.05) !important;
            transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
            outline: none !important;
            user-select: none !important;
            width: auto !important;
            min-width: 0 !important;
            max-width: none !important;
            height: 34px !important;
            box-sizing: border-box !important;
            margin: 0 !important;
          }
          
          .g_premium-pause-btn:hover {
            background: rgba(15, 23, 42, 0.95) !important;
            transform: translateY(-1px) !important;
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.15), 0 0 10px rgba(34, 211, 238, 0.15) !important;
            border-color: rgba(34, 211, 238, 0.4) !important;
            color: #22d3ee !important;
          }
          
          .g_premium-pause-btn:active {
            transform: translateY(1px) scale(0.97) !important;
          }
          
          .g_premium-pause-btn.paused {
            background: rgba(239, 68, 68, 0.12) !important;
            border-color: rgba(239, 68, 68, 0.4) !important;
            color: #f87171 !important;
            box-shadow: 0 4px 16px rgba(239, 68, 68, 0.15), inset 0 1px 0 rgba(239, 68, 68, 0.2), 0 0 12px rgba(239, 68, 68, 0.1) !important;
          }

          .g_premium-pause-btn.paused:hover {
            background: rgba(239, 68, 68, 0.22) !important;
            border-color: rgba(239, 68, 68, 0.6) !important;
            color: #ef4444 !important;
            box-shadow: 0 6px 20px rgba(239, 68, 68, 0.25), inset 0 1px 0 rgba(239, 68, 68, 0.3), 0 0 18px rgba(239, 68, 68, 0.35) !important;
          }
          
          .g_premium-pause-icon {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 24px !important;
            height: 24px !important;
            background: rgba(255, 255, 255, 0.06) !important;
            color: #94a3b8 !important;
            border-radius: 50% !important;
            transition: all 0.25s ease !important;
            flex-shrink: 0 !important;
          }
          
          .g_premium-pause-btn:hover .g_premium-pause-icon {
            background: rgba(34, 211, 238, 0.2) !important;
            color: #22d3ee !important;
            transform: scale(1.05) !important;
          }
          
          .g_premium-pause-btn.paused .g_premium-pause-icon {
             background: rgba(239, 68, 68, 0.15) !important;
             color: #f87171 !important;
          }
          
          .g_premium-pause-btn.paused:hover .g_premium-pause-icon {
             background: rgba(239, 68, 68, 0.25) !important;
             color: #ef4444 !important;
             transform: scale(1.05) !important;
          }

          .g_premium-pause-btn span {
            margin: 0 !important;
            padding: 0 4px 0 0 !important;
            flex-grow: 0 !important;
            width: auto !important;
            display: inline-block !important;
            line-height: 1 !important;
          }

          /* Absolute placement of overlay control */
          .g_pause-control-overlay {
            position: absolute !important;
            top: 16px !important;
            right: 16px !important;
            z-index: 10000000 !important;
            transition: all 0.3s ease !important;
          }

          /* Game specific adjustments for pause button position */
          .g_active-game-wrapper.playing-carrom .g_pause-control-overlay {
            top: 22px !important;
            right: 20px !important;
          }

          .g_active-game-wrapper.playing-memory .g_pause-control-overlay {
            top: 19px !important;
            right: 20px !important;
          }

          @media (max-width: 768px) {
            .g_pause-control-overlay {
              top: calc(max(12px, env(safe-area-inset-top)) + 4px) !important;
              right: max(16px, env(safe-area-inset-right)) !important;
            }

            .g_premium-pause-btn {
              padding: 0 !important;
              width: 36px !important;
              height: 36px !important;
              border-radius: 50% !important;
              justify-content: center !important;
              display: flex !important;
              align-items: center !important;
            }

            .g_premium-pause-btn span {
              display: none !important;
            }

            .g_premium-pause-icon {
              background: transparent !important;
              border-radius: 0 !important;
              width: 100% !important;
              height: 100% !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              margin: 0 !important;
              padding: 0 !important;
            }

            .g_premium-pause-icon svg {
              width: 14px !important;
              height: 14px !important;
            }

            .g_active-game-wrapper.playing-carrom .g_pause-control-overlay {
              top: calc(max(12px, env(safe-area-inset-top)) + 4px) !important;
              right: max(16px, env(safe-area-inset-right)) !important;
            }

            .g_active-game-wrapper.playing-memory .g_pause-control-overlay {
              top: calc(max(0.75rem, env(safe-area-inset-top)) + 4px) !important;
              right: 18px !important;
            }
          }

          /* Premium Pause Menu Overlay styling */
          .g_premium-pause-menu-overlay {
            position: absolute !important;
            inset: 0 !important;
            background: rgba(2, 6, 23, 0.65) !important;
            backdrop-filter: blur(20px) !important;
            -webkit-backdrop-filter: blur(20px) !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            z-index: 10000001 !important;
            animation: g_fade-in 0.35s cubic-bezier(0.16, 1, 0.3, 1) !important;
          }

          .g_premium-pause-menu-card {
            background: rgba(15, 23, 42, 0.45) !important;
            border: 1px solid rgba(255, 255, 255, 0.08) !important;
            backdrop-filter: blur(30px) !important;
            -webkit-backdrop-filter: blur(30px) !important;
            border-radius: 28px !important;
            padding: 40px !important;
            width: 90% !important;
            max-width: 420px !important;
            box-shadow: 
              0 30px 60px rgba(0, 0, 0, 0.6), 
              inset 0 1px 0 rgba(255, 255, 255, 0.05),
              0 0 100px rgba(34, 211, 238, 0.04) !important;
            text-align: center !important;
            animation: g_scale-up 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
            position: relative !important;
            overflow: hidden !important;
          }

          .g_premium-pause-menu-card::before {
            content: '' !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            height: 2px !important;
            background: linear-gradient(90deg, transparent, rgba(34, 211, 238, 0.4), transparent) !important;
          }

          .g_premium-pause-header h2 {
            font-family: var(--font-display, 'Outfit', sans-serif) !important;
            font-size: 2.2rem !important;
            font-weight: 900 !important;
            letter-spacing: 4px !important;
            margin: 0 0 10px 0 !important;
            background: linear-gradient(135deg, #ffffff 30%, #a5f3fc 100%) !important;
            -webkit-background-clip: text !important;
            -webkit-text-fill-color: transparent !important;
            text-shadow: 0 4px 20px rgba(34, 211, 238, 0.15) !important;
          }

          .g_premium-pause-header p {
            font-size: 0.95rem !important;
            color: #94a3b8 !important;
            margin: 0 0 35px 0 !important;
            font-weight: 500 !important;
            letter-spacing: 0.5px !important;
          }

          .g_premium-pause-actions {
            display: flex !important;
            flex-direction: column !important;
            gap: 16px !important;
          }

          .g_premium-pause-btn-action {
            display: flex !important;
            align-items: center !important;
            gap: 16px !important;
            padding: 14px 24px !important;
            border-radius: 18px !important;
            font-size: 0.95rem !important;
            font-weight: 800 !important;
            font-family: var(--font-display, 'Outfit', sans-serif) !important;
            letter-spacing: 1px !important;
            cursor: pointer !important;
            transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
            border: 1px solid transparent !important;
            outline: none !important;
            user-select: none !important;
            text-transform: uppercase !important;
            position: relative !important;
          }

          .g_premium-pause-btn-action.resume {
            background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%) !important;
            color: #ffffff !important;
            box-shadow: 
              0 8px 24px rgba(6, 182, 212, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.2) !important;
          }

          .g_premium-pause-btn-action.resume:hover {
            transform: translateY(-2px) scale(1.02) !important;
            box-shadow: 
              0 14px 32px rgba(6, 182, 212, 0.45),
              0 0 20px rgba(6, 182, 212, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.3) !important;
            filter: brightness(1.1) !important;
          }

          .g_premium-pause-btn-action.resume:active {
            transform: translateY(1px) scale(0.99) !important;
          }

          .g_premium-pause-btn-action.quit {
            background: rgba(255, 255, 255, 0.03) !important;
            border: 1px solid rgba(255, 255, 255, 0.08) !important;
            color: #cbd5e1 !important;
          }

          .g_premium-pause-btn-action.quit:hover {
            background: rgba(239, 68, 68, 0.08) !important;
            border-color: rgba(239, 68, 68, 0.4) !important;
            color: #f87171 !important;
            transform: translateY(-2px) scale(1.02) !important;
            box-shadow: 0 10px 24px rgba(239, 68, 68, 0.15) !important;
          }

          .g_premium-pause-btn-action.quit:active {
            transform: translateY(1px) scale(0.99) !important;
          }

          .g_premium-btn-icon-wrapper {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 28px !important;
            height: 28px !important;
            border-radius: 10px !important;
            background: rgba(255, 255, 255, 0.06) !important;
            transition: all 0.25s ease !important;
            flex-shrink: 0 !important;
          }

          .g_premium-pause-btn-action.resume:hover .g_premium-btn-icon-wrapper {
            background: rgba(255, 255, 255, 0.15) !important;
            transform: rotate(5deg) scale(1.05) !important;
          }

          .g_premium-pause-btn-action.quit:hover .g_premium-btn-icon-wrapper {
            background: rgba(239, 68, 68, 0.15) !important;
            color: #ef4444 !important;
            transform: translateX(-2px) !important;
          }

          @keyframes g_fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes g_scale-up {
            from { opacity: 0; transform: scale(0.92); }
            to { opacity: 1; transform: scale(1); }
          }
        `}</style>
      )}
      <div className={playingGame ? "g_playing-container" : "container"}>
        {playingGame ? (
          <div className={`g_active-game-wrapper playing-${playingGame}`}>
            {/* Global Pause Button Overlay */}
            <div className="g_pause-control-overlay">
              <button
                className={`g_premium-pause-btn ${isPaused ? 'paused' : ''}`}
                onClick={() => setIsPaused(!isPaused)}
              >
                <div className="g_premium-pause-icon">
                  {isPaused ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7 5.23c0-.986 1.085-1.578 1.916-1.04l8.348 5.405c.801.52.801 1.69 0 2.21l-8.348 5.405c-.83.538-1.916-.054-1.916-1.04V5.23z" />
                    </svg>
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="5" y="4" width="4" height="16" rx="1.5" />
                      <rect x="15" y="4" width="4" height="16" rx="1.5" />
                    </svg>
                  )}
                </div>
                <span>{isPaused ? "RESUME" : "PAUSE"}</span>
              </button>
            </div>

            <div className="g_active-game-section" style={{ pointerEvents: isPaused ? 'none' : 'auto', filter: isPaused ? 'blur(4px) brightness(0.6)' : 'none', transition: 'all 0.3s' }}>
              {renderActiveGame()}
            </div>

            {isPaused && (
              <div className="g_premium-pause-menu-overlay">
                <div className="g_premium-pause-menu-card">
                  <div className="g_premium-pause-header">
                    <h2>GAME PAUSED</h2>
                    <p>Take a breath. Your progress is safe.</p>
                  </div>
                  <div className="g_premium-pause-actions">
                    <button className="g_premium-pause-btn-action resume" onClick={() => setIsPaused(false)}>
                      <div className="g_premium-btn-icon-wrapper">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5.23c0-.986 1.085-1.578 1.916-1.04l8.348 5.405c.801.52.801 1.69 0 2.21l-8.348 5.405c-.83.538-1.916-.054-1.916-1.04V5.23z" />
                        </svg>
                      </div>
                      <span>RESUME GAME</span>
                    </button>
                    <button className="g_premium-pause-btn-action quit" onClick={() => { setIsPaused(false); handleBack(); }}>
                      <div className="g_premium-btn-icon-wrapper">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                          <polyline points="16 17 21 12 16 7" />
                          <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                      </div>
                      <span>QUIT TO HUB</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Premium Minimalist Banner - Styled exactly like the user's reference */}
          {window.location.pathname.startsWith("/appgames") ? "" :   <div className="g_games-brand-banner">
              <div className="g_banner-left-accent"></div>              
                <div className="g_banner-content">
                <div className="g_banner-text-group">
                  <div className="g_banner-title-row">
                    <h1 className="g_banner-title">
                      રમત જગત <span className="g_banner-title-en">(RAMATJAGAT)</span>
                    </h1>
                    <div className="g_banner-badge">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      </svg>
                      <span>GAMES</span>
                    </div>
                  </div>
                  <p className="g_banner-subtitle">
                    તમારી મનપસંદ ગેમ્સ એક જ જગ્યાએ રમો અને મેળવો મનોરંજનનો અનોખો રંગીન અનુભવ.
                  </p>
                </div>

                <div className="g_banner-right-group">
                  {/* Category Filter Bar with Custom Premium Dropdown inside Header */}
                  <div className="g_category-filter-bar">
                    <div className="g_filter-dropdown-container">
                      <button
                        className={`g_filter-dropdown-btn ${isDropdownOpen ? 'active' : ''}`}
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        aria-expanded={isDropdownOpen}
                        aria-haspopup="listbox"
                      >
                        <div className="g_selected-info">
                          <span className="g_selected-icon">{getCategoryIcon(activeFilter)}</span>
                          <span className="g_selected-text">{activeFilter==="All" ?"All Games":activeFilter}</span>
                        </div>
                        <svg className={`g_dropdown-chevron ${isDropdownOpen ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>
                      
                      {isDropdownOpen && (
                        <div className="g_filter-dropdown-menu" role="listbox">
                          {categories.map(cat => (
                            <button
                              key={cat}
                              role="option"
                              aria-selected={activeFilter === cat}
                              className={`g_filter-dropdown-item ${activeFilter === cat ? 'active' : ''}`}
                              onClick={() => {
                                setActiveFilter(cat);
                                setIsDropdownOpen(false);
                              }}
                            >
                              <span className="g_dropdown-item-icon">{getCategoryIcon(cat)}</span>
                              <span className="g_dropdown-item-text">{cat}</span>
                              {activeFilter === cat && (
                                <svg className="g_dropdown-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                 
                </div>
              </div>
            </div>}

            {/* Games Grid */}
            <div className="g_nexus-grid" key={activeFilter}>
              {filteredGames.length > 0 ? (
                filteredGames.map((game, index) => (
                  <div key={game.id} className="g_nexus-card" onClick={() => handlePlay(game.id)}>
                    <div className="g_nexus-img-frame">
                      <img src={game.image} alt={game.name} className="g_nexus-img" />
                      <div className="g_nexus-img-overlay"></div>
                    </div>

                    <div className="g_nexus-info-card">
                      <div className="g_nexus-card-header">
                        <h2 className="g_nexus-category">{game.category}</h2>
                        <div className="g_nexus-rating">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                          </svg>
                          <span>{game.rating}</span>
                        </div>
                      </div>

                      <h3 className="g_nexus-title">{game.name}</h3>
                      <p className="g_nexus-desc">{game.description}</p>

                      <div className="g_nexus-card-footer">
                        <div className="g_nexus-stats">
                          <span className="g_nexus-stat-value">{game.difficulty || "Casual"}</span>
                        </div>
                        <div className="g_nexus-play-circle" title="Play Now">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="g_no-games">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="8" y1="15" x2="16" y2="15"></line>
                    <line x1="9" y1="9" x2="9.01" y2="9"></line>
                    <line x1="15" y1="9" x2="15.01" y2="9"></line>
                  </svg>
                  <h3>No games found</h3>
                  <p>Try selecting a different category or check back later!</p>
                  <button onClick={() => setActiveFilter("All")} className="g_filter-btn active">View All Games</button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
      <HomeTaboola key={Math.random} />
    </>
  );
};
export default Games;